import React, { forwardRef, ForwardRefRenderFunction } from "react";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { displayDialog } from "store/actions/utilActions";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { TimeReportingFormTypes, TimeReportingProjectFormTypes } from "models/formTypes";
import { TimeReportingProjectCategoryFormContainerProps } from "models/propTypes/timeReportingProjectPropTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants, TimeReportingConstants } from "utils/constants";
import { FormikProps } from "formik";
import { WintSuperkollAPIModel, WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import TimeReportingProjectCategoryForm from "./timeReportingProjectCategoryForm";
import useCreateDeviationTypeDialog from "components/timeReporting/timeReport/weeklyTimeReport/forms/useCreateDeviationDialog";
import { useTimeReportingProjectService } from "serviceHooks";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
//#endregion
import * as Yup from "yup";
import { DateHelper, EnumHelper, FormHelper, LocalizationHelperInstance } from "utils/helpers";
import { SalaryDeviationsService } from "services";

const TimeReportingProjectCategoryFormContainer: ForwardRefRenderFunction<
  FormHandles,
  TimeReportingProjectCategoryFormContainerProps
> = (props, ref) => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const { isWintSalaryCompany } = useSalaryPermissions();
  const { name, project, isSalaryDeviationActive, otherDeviationTypes } = props;

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const createCustomDeviationDialog = useCreateDeviationTypeDialog();
  const [memberHourlyRateChange, setMemberHourlyRateChange] = React.useState<boolean>(false);

  const earliestStartDate = new Date(project.projectStartDate);
  const latestEndDate = project.projectEndDate ?? DateHelper.getDateMax();

  const formRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (formRef?.current ? formRef.current : ({} as FormHandles)));

  const isNew = React.useMemo(() => !props.name, [props.name]);

  const membersWithFixedPriceMinusDeviation =
    props.project.projectCategoryMembers
      ?.filter(
        (pcm) =>
          pcm.individualBillingModel === ApiModel.TimeReportingPersonProjectBillingModel.FixedPriceMinusDeviations &&
          pcm.projectCategory?.name !== name
      )
      .map((pcm) => pcm.projectMember?.id) ?? [];

  const validationSchema = Yup.object<TimeReportingProjectFormTypes.ProjectCategory>({
    name: Yup.string()
      .required(TranslationKeys.Validations.Required)
      .lowercase()
      .notOneOf(
        project?.projectCategories?.filter((pc) => pc.name !== name).map((pc) => pc.name.toLowerCase()) ?? [],
        TranslationKeys.TimeReportingProject.CategoryAlreadyExists
      ),
    defaultHourlyRate: Yup.number(),
    isDefault: Yup.boolean(),
    isNew: Yup.boolean(),
    id: Yup.number(),
    billingModel: Yup.number(),
    changesApplyFrom: Yup.date().when(
      FormFieldConstants.TimeReportingProject.ProjectCategory.MemberRates,
      ([memberRates], schema: Yup.DateSchema) => {
        return !isNew &&
          memberRates.some((mr: TimeReportingProjectFormTypes.MemberRate) => {
            const initialMemberRate = initialProjectCategoryValues.memberRates?.find((imr) => imr.id === mr.id);
            if (!initialMemberRate) {
              return false;
            }
            return initialMemberRate.hourlyRate !== mr.hourlyRate;
          })
          ? schema.required(TranslationKeys.Validations.Required)
          : schema;
      }
    ),
    memberRates: Yup.array()
      .of(
        Yup.object<TimeReportingProjectFormTypes.MemberRate>({
          id: Yup.number(),
          hours: Yup.number().when("individualBillingModel", ([individualBillingModel], schema: Yup.NumberSchema) =>
            individualBillingModel !== ApiModel.TimeReportingPersonProjectBillingModel.Hourly
              ? schema.required(TranslationKeys.Validations.Required).moreThan(0)
              : schema
          ),
        })
      )
      .test(
        "validateFixedPriceMinusDeviations",
        TranslationKeys.TimeReportingProject.OnlyOneFixedPriceMinusDeviationForPersonProject,
        (val?: TimeReportingProjectFormTypes.MemberRate[]) => {
          if (!val) {
            return true;
          }
          const categoryMembersWithFixedPriceMinusDeviations = val
            .filter(
              (v) =>
                v.individualBillingModel === ApiModel.TimeReportingPersonProjectBillingModel.FixedPriceMinusDeviations
            )
            .map((v) => v.id);
          if (
            membersWithFixedPriceMinusDeviation?.some((m) =>
              categoryMembersWithFixedPriceMinusDeviations?.some((cm) => cm === m)
            )
          ) {
            return false;
          }
          return true;
        }
      ),
    futureMemberRates: Yup.array()
      .of(
        Yup.object<TimeReportingProjectFormTypes.FutureMemberRate>({
          id: Yup.number(),
          validFrom: Yup.date(),
        })
      )
      .uniqueProperties(
        (item: TimeReportingProjectFormTypes.FutureMemberRate) =>
          JSON.stringify({ validFrom: item.validFrom, memberId: item.id }) as any,
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReportingProject.AddedHourlyRateAlreadyExistsForMonth
        )
      )
      .uniqueProperties(
        (item: TimeReportingProjectFormTypes.FutureMemberRate) =>
          JSON.stringify({
            current: item.validFrom && DateHelper.isBefore(item.validFrom, new Date()) ? new Date() : item.validFrom,
            memberId: item.id,
          }) as any,
        LocalizationHelperInstance.getFormattedMessage("more than one month before today")
      ),
    isCatgoryActive: Yup.boolean(),
    shouldEffectSalaryPayout: Yup.boolean(),
    connectedDeviationType: Yup.number().when(
      FormFieldConstants.TimeReportingProject.ProjectCategory.ShouldEffectSalaryPayout,
      ([shouldEffectSalaryPayout], schema: Yup.NumberSchema) =>
        shouldEffectSalaryPayout ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
    connectedDeviationTypeName: Yup.string().when(
      FormFieldConstants.TimeReportingProject.ProjectCategory.ShouldEffectSalaryPayout,
      ([shouldEffectSalaryPayout], schema: Yup.StringSchema) =>
        shouldEffectSalaryPayout ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
    deviationComment: Yup.string(),
    customDeviationName: Yup.string(),
    adjustPreviouslyReportedTime: Yup.boolean(),
  });

  const initialProjectCategoryValues: TimeReportingProjectFormTypes.ProjectCategory = {
    name: props.name ?? "",
    billingModel: props.billingModel ?? 0,
    defaultHourlyRate: props.defaultHourlyRate ?? 0,
    id: props.id,
    isDefault: props.isDefault ?? false,
    isNew: isNew,
    isCatgoryActive: props.isCatgoryActive ?? true,
    endDate: props.endDate,
    memberRates: isNew
      ? project?.projectMembers?.map((pm) => ({
          ...pm,
          selected: true,
          hourlyRate: 0,
          hours: 0,
          individualBillingModel: 0,
          individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(0),
        }))
      : project?.projectCategoryMembers
          ?.filter(
            (pcm) =>
              pcm.projectCategory?.name === props.name &&
              !(pcm.endDate && DateHelper.isBefore(new Date(pcm.endDate), new Date()))
          )
          .map((pcm) => {
            return {
              ...pcm.projectMember,
              selected: false,
              hours: pcm.normalDebitHours,
              individualBillingModel: pcm.individualBillingModel,
              individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(
                pcm.individualBillingModel
              ),
              hourlyRate: pcm.hourlyRates?.find((hr) => hr.isCurrent)?.hourlyRate ?? 0,
              currentRate: pcm.hourlyRates?.find((hr) => hr.isCurrent)?.hourlyRate ?? 0,
            } as TimeReportingProjectFormTypes.MemberRate;
          }),
    futureMemberRates: project?.projectCategoryMembers
      ?.filter((pcm) => pcm.projectCategory?.name === props.name && !!pcm.hourlyRates)
      .flatMap((pcm) => pcm.hourlyRates?.filter((hr) => !hr.isCurrent).map((hr) => ({ ...hr, pcm: pcm })))
      .map(
        (hr) =>
          ({
            ...hr?.pcm?.projectMember,
            hourlyRateId: hr?.id,
            hourlyRate: hr?.hourlyRate,
            hours: hr?.pcm.normalDebitHours,
            validFrom: hr?.fromDate,
            individualBillingModel: hr?.pcm.individualBillingModel,
            currentRate: hr?.pcm.hourlyRates?.find((pcmHourlyRate) => pcmHourlyRate.isCurrent)?.hourlyRate ?? 0,
          }) as TimeReportingProjectFormTypes.FutureMemberRate
      )
      .filter((hr) => !!hr.validFrom && (DateHelper.isBefore(new Date(), new Date(hr.validFrom)) || !hr.hourlyRateId)),
    categoryMembers: project?.projectMembers?.map((pm) => {
      const memberRelation = project.projectCategoryMembers?.find(
        (pcm) => pm.id === pcm.projectMember?.id && pcm.projectCategory?.name === props.name
      );
      let isSelected = isNew;

      if (memberRelation) {
        isSelected = memberRelation?.endDate
          ? DateHelper.isBefore(DateHelper.today(), new Date(memberRelation?.endDate))
          : true;
      }
      return {
        ...pm,
        hourlyRate: memberRelation?.hourlyRates?.find((hr) => hr.isCurrent)?.hourlyRate ?? 0,
        hours: memberRelation?.normalDebitHours,
        individualBillingModel: memberRelation?.individualBillingModel,
        selected: isSelected,
        accessUntil: memberRelation?.endDate,
        currentRate: memberRelation?.hourlyRates?.find((hr) => hr.isCurrent)?.hourlyRate ?? 0,
      };
    }),
    // Wintlön ignores category deviation links and Core keeps the stored one, so the form shows none.
    shouldEffectSalaryPayout: !isWintSalaryCompany && (props.shouldEffectSalaryPayout ?? false),
    connectedDeviationType: props.connectedDeviationType ?? undefined,
    connectedDeviationTypeName: props.connectedDeviationTypeName,
    deviationComment: props.deviationComment,
    customDeviationName: props.customDeviationName,
    adjustPreviouslyReportedTime: props.adjustPreviouslyReportedTime,
  };

  const initialCategory = props.initialProject?.projectCategories?.find(
    // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
    (pc) => pc.id == initialProjectCategoryValues.id
  );

  const getBillingModels = () => {
    const billingModels = EnumHelper.getNumericItemValues(ApiModel.BillingModelType).map(
      (item) =>
        ({
          id: item,
          value: LocalizationHelperInstance.getTranslatedEnumValue("TimeReportingProject.CategoryInvoiceType", item),
        }) as SelectionItem
    );

    return billingModels;
  };

  const onMemberSelectAllActivationChange = (
    name: string,
    updatedValue: TimeReportingProjectFormTypes.CategoryMember[]
  ) => {
    const values: TimeReportingProjectFormTypes.ProjectCategory =
      formRef.current?.getValues && formRef.current.getValues();

    const newValues = [...updatedValue];
    const newMemberRates: TimeReportingProjectFormTypes.MemberRate[] = [];
    const categoryMembers: TimeReportingProjectFormTypes.CategoryMember[] = [];

    newValues.forEach((uv) => {
      let categoryMember = { ...uv };
      if (uv.selected) {
        const existingMemberRate = values?.memberRates?.find((pm) => pm.id === uv.id);
        if (existingMemberRate) {
          newMemberRates.push(existingMemberRate);
        } else {
          const memberRate = getMemberRateOnSelectionChange(uv, values);
          memberRate && newMemberRates.push(memberRate);
          categoryMember = {
            ...categoryMember,
            individualBillingModel: memberRate.individualBillingModel,
            hourlyRate: memberRate.hourlyRate,
            hours: memberRate.hours,
            accessUntil: undefined,
          };
        }
      } else {
        const defaultHourlyRate = project?.projectCategoryMembers
          ?.find((pcm) => pcm.projectCategory?.name === props.name && pcm.projectMember?.id === uv.id)
          ?.hourlyRates?.find((hr) => !hr.fromDate);
        if (!defaultHourlyRate) {
          categoryMember = { ...categoryMember, individualBillingModel: undefined };
        } else {
          categoryMember = {
            ...categoryMember,
            accessUntil: categoryMember.accessUntil ?? DateHelper.getLastDayOfMonth(new Date()),
          };
        }
      }
      categoryMembers.push(categoryMember);
    });

    formRef.current?.setValues &&
      formRef.current.setValues({ ...values, memberRates: [...newMemberRates], categoryMembers: [...categoryMembers] });
  };

  const onMemberActivationChange = (
    name: string,
    updatedValue: boolean,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectCategory>,
    userInput?: boolean
  ) => {
    if (!userInput) {
      return;
    }
    const { values } = formikProps;

    const fieldInfo = FormHelper.getTableRowFieldInfo(name);

    const newValues = values.categoryMembers?.map((cm, index) =>
      index === fieldInfo?.rowIndex ? { ...cm, selected: updatedValue } : cm
    );

    onMemberSelectAllActivationChange(FormFieldConstants.TimeReportingProject.ProjectCategory.CategoryMembers, [
      ...(newValues ?? []),
    ]);
  };

  const getMemberRateOnSelectionChange = (
    categoryMember: TimeReportingProjectFormTypes.CategoryMember,
    category?: TimeReportingProjectFormTypes.ProjectCategory
  ) => {
    const existingMemberRelation = project?.projectCategoryMembers?.find(
      (pcm) => pcm.projectCategory?.name === props.name && pcm.projectMember?.id === categoryMember.id
    );
    if (existingMemberRelation) {
      return {
        ...existingMemberRelation.projectMember,
        hours: existingMemberRelation.normalDebitHours,
        individualBillingModel: existingMemberRelation.individualBillingModel,
        individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(
          existingMemberRelation.individualBillingModel
        ),
        hourlyRate:
          existingMemberRelation.hourlyRates?.find((hr) => !hr.fromDate)?.hourlyRate ??
          existingMemberRelation.normalDebitHours,
      } as TimeReportingProjectFormTypes.MemberRate;
    }

    const freshMember = project?.projectMembers?.find((pm) => pm.id === categoryMember.id);
    return {
      ...freshMember,
      hours: 0,
      hourlyRate: category?.defaultHourlyRate,
      individualBillingModel: 0,
      individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(0),
      selected: isNew,
    } as TimeReportingProjectFormTypes.MemberRate;
  };

  const onDefaultHourlyRateChange = (
    name: string,
    updatedValue: number,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectCategory>
  ) => {
    if (!isNew || memberHourlyRateChange) {
      return;
    }

    const { values, setValues } = formikProps;

    const newMemberRates = values.memberRates?.map((mr) => ({ ...mr, hourlyRate: updatedValue }));
    const categoryMembers = values.categoryMembers?.map((cm) =>
      cm.selected
        ? ({
            ...cm,
            hourlyRate: updatedValue,
            individualBillingModel: cm.individualBillingModel ?? 0,
          } as TimeReportingProjectFormTypes.CategoryMember)
        : cm
    );

    setValues({ ...values, memberRates: newMemberRates, categoryMembers: categoryMembers });
  };

  const onChangesApplyMonthChange = (
    name: string,
    updatedValue: Date,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectCategory>
  ) => {
    const { values, setValues } = formikProps;

    const newFutureRates: TimeReportingProjectFormTypes.FutureMemberRate[] =
      values.futureMemberRates?.map((fmr) => ({
        ...fmr,
        currentRate: values.categoryMembers?.find((cm) => cm.id === fmr.id)?.currentRate,
      })) ?? [];
    values.memberRates
      ?.filter((mr) => mr.selected)
      .forEach((mr) => {
        const existingFutureRateIndex = values.futureMemberRates?.findIndex(
          (fmr) => fmr.id === mr.id && fmr.validFrom && DateHelper.isEqual(fmr.validFrom, updatedValue)
        );
        if (existingFutureRateIndex !== undefined && existingFutureRateIndex > -1) {
          newFutureRates[existingFutureRateIndex].hourlyRate = mr.hourlyRate;
        } else {
          newFutureRates.push({ ...mr, validFrom: new Date(updatedValue) });
        }
      });

    const updatedCategoryMembers =
      values.categoryMembers?.map((cm) =>
        // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
        values.memberRates?.find((mr) => mr.id == cm.id)?.selected
          ? ({ ...cm, hourlyRate: cm.currentRate } as TimeReportingProjectFormTypes.CategoryMember)
          : cm
      ) ?? [];

    setValues({ ...values, futureMemberRates: [...newFutureRates], categoryMembers: [...updatedCategoryMembers] });
  };

  const onHourlyRateChange = (
    name: string,
    updatedValue: number,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectCategory>
  ) => {
    const { values, setFieldValue } = formikProps;
    const fieldInfo = FormHelper.getTableRowFieldInfo(name);

    const memberRate = values.memberRates?.find((mr, index) => index === fieldInfo?.rowIndex);
    if (values.changesApplyFrom) {
      const futureMemberRates = [...(values.futureMemberRates ?? [])];

      const existingFutureRateIndex = values.futureMemberRates?.findIndex(
        (smr) =>
          smr.id === memberRate?.id &&
          smr.validFrom &&
          values.changesApplyFrom &&
          DateHelper.isEqual(smr.validFrom, values.changesApplyFrom)
      );
      if (existingFutureRateIndex !== undefined && existingFutureRateIndex > -1) {
        futureMemberRates[existingFutureRateIndex].hourlyRate = updatedValue;
      } else {
        const newFutureMemberRate = {
          ...memberRate,
          validFrom: values.changesApplyFrom,
          hourlyRate: updatedValue,
        } as TimeReportingProjectFormTypes.FutureMemberRate;
        futureMemberRates.push(newFutureMemberRate);
      }

      setFieldValue(FormFieldConstants.TimeReportingProject.ProjectCategory.FutureMemberRates, [...futureMemberRates]);
    }
    if (isNew) {
      const categoryMembers =
        values.categoryMembers?.map((cm) =>
          cm.id === memberRate?.id
            ? { ...cm, hourlyRate: updatedValue, individualBillingModel: memberRate.individualBillingModel }
            : cm
        ) ?? [];
      setFieldValue(FormFieldConstants.TimeReportingProject.ProjectCategory.CategoryMembers, [...categoryMembers]);
    }
    setMemberHourlyRateChange(true);
  };

  const onMemberRateHoursChange = (
    name: string,
    updatedValue: number,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectCategory>,
    userInput?: boolean
  ) => {
    if (!userInput) {
      return;
    }
    const { values, setValues } = formikProps;
    const fieldInfo = FormHelper.getTableRowFieldInfo(name);

    // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
    const memberRate = values?.memberRates?.find((mr, index) => index == fieldInfo?.rowIndex);

    const newFutureMemberRates =
      values?.futureMemberRates?.map((fmr) =>
        // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
        fmr.id == memberRate?.id
          ? ({ ...fmr, hours: updatedValue } as TimeReportingProjectFormTypes.FutureMemberRate)
          : fmr
      ) ?? [];

    addFutureRates(newFutureMemberRates, values, memberRate, memberRate?.individualBillingModel, updatedValue);

    const updatedCategoryMembers =
      values.categoryMembers?.map((cm) => (cm.id === memberRate?.id ? { ...cm, hours: updatedValue } : cm)) ?? [];

    setValues({
      ...values,
      futureMemberRates: [...newFutureMemberRates],
      categoryMembers: [...updatedCategoryMembers],
    });
  };

  const onIndividualBillingModelChange = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectCategory>
  ) => {
    const { values, setFieldValue } = formikProps;
    const fieldInfo = FormHelper.getTableRowFieldInfo(name);
    const memberRate = values.memberRates?.find((mr, index) => index === fieldInfo?.rowIndex);
    let debitHours = memberRate?.hours ?? 0;

    if (Number(item.id) !== ApiModel.TimeReportingPersonProjectBillingModel.Hourly) {
      debitHours = TimeReportingConstants.NormalHoursPerMonth;
    }

    const futureMemberRates =
      values.futureMemberRates?.map((fmr) =>
        fmr.id === memberRate?.id ? { ...fmr, individualBillingModel: Number(item.id), hours: debitHours } : fmr
      ) ?? [];

    addFutureRates(futureMemberRates, values, memberRate, Number(item.id));

    const updatedCategoryMembers =
      values?.categoryMembers?.map((cm) =>
        cm.id === memberRate?.id ? { ...cm, individualBillingModel: Number(item.id), hours: debitHours } : cm
      ) ?? [];

    const updatedMemberRates =
      values.memberRates?.map((mr) =>
        mr.id === memberRate?.id
          ? {
              ...mr,
              hours: debitHours,
              individualBillingModel: item.id,
              individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(Number(item.id)),
            }
          : mr
      ) ?? [];

    setFieldValue(FormFieldConstants.TimeReportingProject.ProjectCategory.MemberRates, [...updatedMemberRates]);
    setFieldValue(FormFieldConstants.TimeReportingProject.ProjectCategory.FutureMemberRates, [...futureMemberRates]);
    setFieldValue(FormFieldConstants.TimeReportingProject.ProjectCategory.CategoryMembers, [...updatedCategoryMembers]);
  };

  const addFutureRates = (
    futureMemberRates: TimeReportingProjectFormTypes.FutureMemberRate[],
    values: TimeReportingProjectFormTypes.ProjectCategory,
    memberRate?: TimeReportingProjectFormTypes.MemberRate,
    individualBillingModel?: ApiModel.TimeReportingPersonProjectBillingModel,
    debitHours?: number
  ) => {
    if (values.changesApplyFrom) {
      const existingFutureRate = values.futureMemberRates?.find(
        (smr) =>
          smr.id === memberRate?.id &&
          smr.validFrom &&
          values.changesApplyFrom &&
          DateHelper.isEqual(smr.validFrom, values.changesApplyFrom)
      );
      if (!existingFutureRate) {
        const newFutureMemberRate = {
          ...memberRate,
          validFrom: values.changesApplyFrom,
          individualBillingModel: individualBillingModel ?? memberRate?.individualBillingModel,
          hours: debitHours ?? memberRate?.hours,
        } as TimeReportingProjectFormTypes.FutureMemberRate;
        futureMemberRates.push(newFutureMemberRate);
      }
    }
  };

  const handleSubmit = (values: TimeReportingProjectFormTypes.ProjectCategory) => {
    let newValues = { ...values };
    if (isAdjustPreviouslyReportedTimeHidden(values)) {
      newValues = { ...newValues, adjustPreviouslyReportedTime: false };
    }
    if (!isSalaryDeviationActive) {
      newValues = {
        ...newValues,
        shouldEffectSalaryPayout: false,
        connectedDeviationType: undefined,
        deviationComment: undefined,
      };
    }
    return props.handleSubmit && props.handleSubmit(newValues, props.name);
  };

  const onSelectOtherDeviation = (
    item: AutocompleteListItem | SelectionItem,
    name: string,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectCategory>
  ) => {
    const { setFieldValue } = formikProps;

    // eslint-disable-next-line eqeqeq --- checked against different types
    if (item.id == WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation) {
      dispatch(
        displayDialog(
          createCustomDeviationDialog((deviationValue: TimeReportingFormTypes.CreateDeviationType) =>
            onSubmitCustomDeviation(deviationValue, formikProps)
          )
        )
      );
    } else {
      const comment = LocalizationHelperInstance.getFormattedMessage(`SalaryDeviation.TypeDescription_${item.id}`);
      setFieldValue(FormFieldConstants.TimeReportingProject.ProjectCategory.DeviationComment, comment);
      setFieldValue(FormFieldConstants.TimeReportingProject.ProjectCategory.CustomDeviationName, "");
    }
  };

  const onSubmitCustomDeviation = (
    deviationValue: TimeReportingFormTypes.CreateDeviationType,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectCategory>
  ) => {
    const { values, setValues } = formikProps;
    const type = WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation;
    const name = SalaryDeviationsService.getSalaryDeviationTypeText(type, deviationValue.name);

    setValues({
      ...values,
      connectedDeviationType: WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation,
      connectedDeviationTypeName: name,
      deviationComment: deviationValue.comment,
      customDeviationName: deviationValue.name,
    });
  };

  const isAdjustPreviouslyReportedTimeHidden = (values: TimeReportingProjectFormTypes.ProjectCategory) => {
    if (!initialProjectCategoryValues.id) {
      return true;
    }

    if (!(isSalaryDeviationActive && otherDeviationTypes && otherDeviationTypes?.length > 0)) {
      return true;
    }

    const currentValues = {
      shouldEffectSalaryPayout: values.shouldEffectSalaryPayout,
      type: values.connectedDeviationType,
      typeName: values.connectedDeviationTypeName,
      comment: values.deviationComment,
    };
    const initialValues = {
      shouldEffectSalaryPayout: initialCategory?.shouldEffectSalaryPayout,
      type: initialCategory?.connectedDeviationType,
      typeName: initialCategory?.connectedDeviationTypeName,
      comment: initialCategory?.deviationComment,
    };
    return JSON.stringify(currentValues) === JSON.stringify(initialValues);
  };

  const isShouldEffectSalaryPayoutHidden = (values: TimeReportingProjectFormTypes.ProjectCategory) => {
    if (isWintSalaryCompany) {
      return true;
    }
    if (!!initialCategory?.shouldEffectSalaryPayout) {
      return false;
    }
    return !(isSalaryDeviationActive && otherDeviationTypes && otherDeviationTypes?.length > 0);
  };

  return (
    <TimeReportingProjectCategoryForm
      ref={formRef}
      {...props}
      isNew={isNew}
      billingModels={getBillingModels()}
      defaultValues={initialProjectCategoryValues}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      onMemberActivationChange={onMemberActivationChange}
      onMemberSelectAllActivationChange={onMemberSelectAllActivationChange}
      onDefaultHourlyRateChange={onDefaultHourlyRateChange}
      onChangesApplyMonthChange={onChangesApplyMonthChange}
      onHourlyRateChange={onHourlyRateChange}
      onMemberRateHoursChange={onMemberRateHoursChange}
      onIndividualBillingModelChange={onIndividualBillingModelChange}
      earliestStartDate={earliestStartDate}
      latestEndDate={latestEndDate}
      isSalaryDeviationActive={props.isSalaryDeviationActive}
      otherDeviationTypes={props.otherDeviationTypes}
      onSelectOtherDeviation={onSelectOtherDeviation}
      isAdjustPreviouslyReportedTimeHidden={isAdjustPreviouslyReportedTimeHidden}
      isShouldEffectSalaryPayoutHidden={isShouldEffectSalaryPayoutHidden}
    />
  );
};

export default forwardRef(TimeReportingProjectCategoryFormContainer);
